// src/pages/UserAdmin/TemplatePreview.js — bolha do WhatsApp em tempo real.
//
// Mostra o template como o cliente final vai ver, com as variáveis já
// trocadas pelos exemplos. É esse o ponto: {{1}} não diz nada sobre como a
// frase vai soar, "Olá João" diz.
//
// Componente de apresentação puro — recebe o estado do formulário e desenha.
// Nada de estado próprio, nada de rede, pra poder ser usado também na aba de
// listagem, onde o template vem da Meta em vez de um formulário.

import { useEffect, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { FiClipboard, FiExternalLink, FiImage, FiPhone } from 'react-icons/fi';
import { extractVars, fillVars } from '../../utils/templateMeta';

// Cores do WhatsApp — a bolha verde do remetente e o fundo do papel de parede.
const BUBBLE_BG = '#d9fdd3';
const WALLPAPER = '#e5ddd5';
const BUTTON_TEXT = '#0a7cff';

/**
 * Formatação do WhatsApp (*negrito*, _itálico_, ~tachado~, ```mono```).
 *
 * Escapa o HTML ANTES de aplicar os marcadores: o texto vem do que a pessoa
 * digitou, e sem isso um `<script>` no corpo do template viraria script de
 * verdade nesta tela.
 */
function formatWhatsApp(text) {
  if (!text) return '';
  let html = String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  html = html.replace(
    /```([^`]+)```/g,
    '<code style="background:rgba(0,0,0,.06);padding:1px 4px;border-radius:3px;font-family:monospace;font-size:.9em">$1</code>'
  );
  html = html.replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>');
  html = html.replace(/_([^_\n]+)_/g, '<em>$1</em>');
  html = html.replace(/~([^~\n]+)~/g, '<s>$1</s>');
  // Variável ainda sem exemplo fica destacada em vez de passar despercebida:
  // é exatamente o campo que falta preencher pra Meta aprovar.
  html = html.replace(
    /\{\{\s*\d+\s*\}\}/g,
    (match) =>
      `<span style="background:#fde68a;color:#92400e;border-radius:3px;padding:0 3px;font-weight:600">${match}</span>`
  );
  return html.replace(/\n/g, '<br/>');
}

function PreviewButton({ icon, children }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      spacing={0.75}
      sx={{
        py: 1.1,
        color: BUTTON_TEXT,
        fontSize: '0.86rem',
        fontWeight: 500,
        borderTop: '1px solid rgba(0,0,0,0.08)',
      }}
    >
      {icon}
      <span>{children}</span>
    </Stack>
  );
}

export default function TemplatePreview({ form, imageResolver }) {
  // Trocar a imagem recomeça a avaliação de "carrega ou não" — senão uma
  // falha anterior deixaria a prévia em modo placeholder pra sempre.
  const urlImagem = form.header?.imageUrl || '';
  const [imagemFalhou, setImagemFalhou] = useState(false);
  useEffect(() => { setImagemFalhou(false); }, [urlImagem]);

  const examples = form.examples || {};
  const headerType = form.header?.type || 'NONE';
  const headerText = headerType === 'TEXT' ? (form.header.text || '') : '';
  const bodyText = form.body || '';
  const hasContent = Boolean(bodyText.trim() || headerText.trim() || form.header?.imageUrl);

  // O cabeçalho tem exemplo próprio (a Meta só permite uma variável nele),
  // guardado fora do mapa do corpo. Aqui montamos um mapa de um item só pra
  // reusar o mesmo fillVars.
  const headerVars = extractVars(headerText);
  const headerFilled = headerVars.length
    ? fillVars(headerText, { [headerVars[0]]: form.headerExample })
    : headerText;

  const buttons =
    form.buttonMode === 'QUICK_REPLY'
      ? (form.quickReplies || []).filter((b) => (b.text || '').trim())
      : form.buttonMode === 'CTA'
        ? (form.ctas || []).filter((b) => (b.text || '').trim())
        : form.buttonMode === 'FORM'
          ? [{ type: 'FLOW', text: (form.flowButton?.text || '').trim() || 'Abrir formulário' }]
          : [];

  return (
    <Box
      sx={{
        bgcolor: WALLPAPER,
        borderRadius: 3,
        p: 2.5,
        border: '1px solid #e2e8f0',
        // Textura discreta do papel de parede do WhatsApp, sem imagem
        // externa: dois gradientes cruzados de baixa opacidade.
        backgroundImage:
          'radial-gradient(rgba(255,255,255,.35) 1px, transparent 1px), radial-gradient(rgba(0,0,0,.02) 1px, transparent 1px)',
        backgroundSize: '18px 18px, 18px 18px',
        backgroundPosition: '0 0, 9px 9px',
      }}
    >
      {!hasContent ? (
        <Typography
          variant="caption"
          sx={{ color: '#64748b', display: 'block', textAlign: 'center', py: 4 }}
        >
          O preview aparece aqui conforme você escreve.
        </Typography>
      ) : (
        <Box
          sx={{
            bgcolor: BUBBLE_BG,
            borderRadius: '8px 8px 8px 2px',
            maxWidth: 330,
            ml: 'auto',
            overflow: 'hidden',
            boxShadow: '0 1px 1px rgba(11,20,26,0.15)',
          }}
        >
          <Box sx={{ p: 1.25 }}>
            {/* Cabeçalho de imagem. O `onError` não é zelo excessivo: na
                EDIÇÃO a URL que a Meta devolve aponta pro CDN dela
                (scontent.whatsapp.net), que não abre de fora, e sem isto a
                prévia mostrava o ícone de imagem quebrada. Um retângulo
                que diz "imagem no topo" representa melhor o que o cliente
                vai ver do que um ícone de erro. */}
            {headerType === 'IMAGE' && form.header.imageUrl && (
              imagemFalhou ? (
                <Box
                  sx={{
                    width: '100%', height: 120, borderRadius: 1.5, mb: 1,
                    bgcolor: '#e9edef', color: '#8696a0',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: 0.5,
                  }}
                >
                  <FiImage size={22} />
                  <Box component="span" sx={{ fontSize: '0.72rem', fontWeight: 600 }}>
                    Imagem no topo da mensagem
                  </Box>
                </Box>
              ) : (
                <Box
                  component="img"
                  src={imageResolver ? imageResolver(form.header.imageUrl) : form.header.imageUrl}
                  alt=""
                  onError={() => setImagemFalhou(true)}
                  sx={{
                    width: '100%',
                    maxHeight: 170,
                    objectFit: 'cover',
                    borderRadius: 1.5,
                    display: 'block',
                    mb: 1,
                  }}
                />
              )
            )}

            {headerType === 'TEXT' && headerText.trim() && (
              <Box
                sx={{ fontWeight: 700, fontSize: '0.92rem', color: '#111b21', mb: 0.75 }}
                dangerouslySetInnerHTML={{ __html: formatWhatsApp(headerFilled) }}
              />
            )}

            <Box
              sx={{
                fontSize: '0.9rem',
                lineHeight: 1.45,
                color: '#111b21',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
              dangerouslySetInnerHTML={{ __html: formatWhatsApp(fillVars(bodyText, examples)) }}
            />

            {(form.footer || '').trim() && (
              <Typography
                sx={{ fontSize: '0.78rem', color: 'rgba(17,27,33,0.5)', mt: 0.75 }}
              >
                {form.footer}
              </Typography>
            )}

            <Typography
              sx={{
                fontSize: '0.68rem',
                color: 'rgba(17,27,33,0.45)',
                textAlign: 'right',
                mt: 0.25,
              }}
            >
              {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} ✓✓
            </Typography>
          </Box>

          {buttons.map((button, index) => (
            <PreviewButton
              key={button.id || index}
              icon={
                button.type === 'URL' ? (
                  <FiExternalLink size={13} />
                ) : button.type === 'PHONE_NUMBER' ? (
                  <FiPhone size={13} />
                ) : button.type === 'FLOW' ? (
                  <FiClipboard size={13} />
                ) : null
              }
            >
              {button.text}
            </PreviewButton>
          ))}
        </Box>
      )}
    </Box>
  );
}
